import { inject } from '@angular/core';

import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { from, switchMap } from 'rxjs';

import { setErrorStatusFor, setPendingStatusFor, setSuccessStatusFor, withRequestStatus } from '@drf/core';

import { EvmWalletAccessService } from '../services';

export interface Erc20BalanceRequest {
  chainId: number;
  tokenAddress: string;
}

export interface NativeBalanceRequest {
  chainId: number;
}

interface WalletBalancesState {
  connectedChainId: number | null;
  erc20Balances: Record<string, WalletBalanceSnapshot>;
  nativeBalances: Record<number, WalletBalanceSnapshot>;
  walletAddress: string | null;
}

interface WalletBalanceSnapshot {
  balance: string;
  decimals: number;
}

const initialState: WalletBalancesState = {
  connectedChainId: null,
  erc20Balances: {},
  nativeBalances: {},
  walletAddress: null,
};

export const WalletBalancesStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withRequestStatus(['loadConnectedWalletAddress', 'loadErc20Balance', 'loadNativeBalance']),
  withMethods((store, walletAccess = inject(EvmWalletAccessService)) => {
    const loadConnectedWalletAddress = rxMethod<void>(
      switchMap(() => {
        patchState(store, setPendingStatusFor('loadConnectedWalletAddress'));

        return from(walletAccess.getConnectedWalletIdentity()).pipe(
          tapResponse({
            next: identity => {
              patchState(
                store,
                {
                  connectedChainId: identity.chainId,
                  walletAddress: identity.walletAddress,
                },
                setSuccessStatusFor('loadConnectedWalletAddress'),
              );
            },
            error: error => {
              patchState(store, setErrorStatusFor('loadConnectedWalletAddress', toWalletBalanceErrorMessage(error)));
            },
          }),
        );
      }),
    );
    const loadErc20Balance = rxMethod<Erc20BalanceRequest>(
      switchMap(request => {
        patchState(
          store,
          {
            erc20Balances: withoutErc20Balance(store.erc20Balances(), request),
          },
          setPendingStatusFor('loadErc20Balance'),
        );

        return from(walletAccess.getErc20Balance(request.chainId, request.tokenAddress)).pipe(
          tapResponse({
            next: result => {
              patchState(
                store,
                {
                  erc20Balances: {
                    ...store.erc20Balances(),
                    [getErc20BalanceKey(request)]: {
                      balance: result.balance,
                      decimals: result.decimals,
                    },
                  },
                  walletAddress: result.walletAddress,
                },
                setSuccessStatusFor('loadErc20Balance'),
              );
            },
            error: error => {
              patchState(store, setErrorStatusFor('loadErc20Balance', toWalletBalanceErrorMessage(error)));
            },
          }),
        );
      }),
    );
    const loadNativeBalance = rxMethod<NativeBalanceRequest>(
      switchMap(request => {
        patchState(
          store,
          {
            nativeBalances: withoutNativeBalance(store.nativeBalances(), request),
          },
          setPendingStatusFor('loadNativeBalance'),
        );

        return from(walletAccess.getNativeBalance(request.chainId)).pipe(
          tapResponse({
            next: result => {
              patchState(
                store,
                {
                  nativeBalances: {
                    ...store.nativeBalances(),
                    [request.chainId]: {
                      balance: result.balance,
                      decimals: result.decimals,
                    },
                  },
                  walletAddress: result.walletAddress,
                },
                setSuccessStatusFor('loadNativeBalance'),
              );
            },
            error: error => {
              patchState(store, setErrorStatusFor('loadNativeBalance', toWalletBalanceErrorMessage(error)));
            },
          }),
        );
      }),
    );

    const getErc20Balance = (request: Erc20BalanceRequest): string => store.erc20Balances()[getErc20BalanceKey(request)]?.balance ?? '0';
    const getNativeBalance = (request: NativeBalanceRequest): string => store.nativeBalances()[request.chainId]?.balance ?? '0';

    return {
      clear(): void {
        patchState(store, initialState);
      },
      getErc20Balance,
      getErc20BalanceNumber(request: Erc20BalanceRequest): number {
        const balance = Number(getErc20Balance(request));

        return Number.isFinite(balance) ? balance : 0;
      },
      getErc20Decimals(request: Erc20BalanceRequest): number | null {
        return store.erc20Balances()[getErc20BalanceKey(request)]?.decimals ?? null;
      },
      getNativeBalance,
      getNativeBalanceNumber(request: NativeBalanceRequest): number {
        const balance = Number(getNativeBalance(request));

        return Number.isFinite(balance) ? balance : 0;
      },
      getNativeDecimals(request: NativeBalanceRequest): number | null {
        return store.nativeBalances()[request.chainId]?.decimals ?? null;
      },
      loadConnectedWalletAddress,
      loadErc20Balance,
      loadNativeBalance,
    };
  }),
);

function getErc20BalanceKey({ chainId, tokenAddress }: Erc20BalanceRequest): string {
  return `${chainId}:${tokenAddress.toLowerCase()}`;
}

function withoutErc20Balance(
  balances: Record<string, WalletBalanceSnapshot>,
  request: Erc20BalanceRequest,
): Record<string, WalletBalanceSnapshot> {
  const remainingBalances = { ...balances };
  delete remainingBalances[getErc20BalanceKey(request)];

  return remainingBalances;
}

function withoutNativeBalance(
  balances: Record<number, WalletBalanceSnapshot>,
  request: NativeBalanceRequest,
): Record<number, WalletBalanceSnapshot> {
  const remainingBalances = { ...balances };
  delete remainingBalances[request.chainId];

  return remainingBalances;
}

function toWalletBalanceErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }

  return 'Failed to load wallet balance.';
}
